import { Home, HeartHandshake, Building2, Church, Medal } from "lucide-react";

export const SITE = "https://406mobilemechanic.com";

export const towns = ["Great Falls", "Black Eagle", "Malmstrom AFB", "Vaughn", "Belt", "Cascade", "Ulm", "Sun River", "Dutton", "Fort Benton"] as const;

export const townNotes: Record<(typeof towns)[number], string> = {
  "Great Falls": "Home base. Driveways, apartment lots, job sites and roadsides all over town.",
  "Black Eagle": "Right across the Missouri River — a quick trip for on-site repairs.",
  "Malmstrom AFB": "Service for military members and families in the area, with a military discount.",
  Vaughn: "West of Great Falls along I-15 — no need to tow into town.",
  Belt: "East of Great Falls. Breakdowns, no-starts and maintenance handled where you are.",
  Cascade: "South down I-15. Call or text and we'll plan the trip out to you.",
  Ulm: "Just southwest of Great Falls — close enough for same-day help when the schedule allows.",
  "Sun River": "West of town. Farm, ranch and family vehicles serviced on site.",
  Dutton: "North on I-15. We've made last-minute emergency trips out to Dutton before.",
  "Fort Benton": "Northeast along the Missouri. Larger jobs and inspections by arrangement.",
};

export const steps = [
  { n: "01", t: "Call or Text", d: "Tell us what's going on and where you are — 7 days, 8 AM–9 PM." },
  { n: "02", t: "Fast Quote", d: "We source parts and send a labor quote right away." },
  { n: "03", t: "We Come to You", d: "Diagnose on site and show you the exact issue firsthand." },
  { n: "04", t: "Job Done Right", d: "Repaired the first time, on time." },
];

export const why = [
  "Shows you the problem firsthand",
  "Shows up when he says he will",
  "Fast, friendly service",
  "Open 7 days a week",
  "Goes where other mechanics won't, in conditions they won't",
  "Honest, reasonable quotes",
  "Serving Montana since 2020",
];

export const audiences = [
  { icon: Home, t: "Homeowners & Families" },
  { icon: HeartHandshake, t: "Seniors" },
  { icon: Building2, t: "Local Businesses & Fleets" },
  { icon: Church, t: "Churches & Organizations" },
  { icon: Medal, t: "Military Families", note: "Military discount" },
];

export type Review = { name: string; text: string };

export const reviews: Record<"mccleary" | "cindy" | "deb", Review> = {
  mccleary: {
    name: "MCleary",
    text: "OMG!! This guy is like Magic - he was so calm and really you could see him THINKING things through - like a neurosurgeon!! We have a VERY complicated vehicle - 1988 Volkswagen w/ a Subaru conversion - two battery systems and LOTS of complex (hard to get to) wiring!! He just showed up when he said he was going to and methodically went through the electrical system! Then he FOUND the issue and SOLVED the problem. We are both SO impressed and grateful for skills & calmness. We were a long way from home ...impressive work truck with all the bells & whistles...super clean and super SMART dude!! Thank you Josh...we appreciate you",
  },
  cindy: { name: "Cindy Fuson", text: "Excellent mechanic! Knows his stuff! Knew right what to do and what he needed to get the repair done quick! Was so very prompt, helpful and polite. 10 out of 10 would recommend Josh for your repair needs." },
  deb: { name: "Deb Sween", text: "Did good work and was friendly. Was able to travel out to Dutton last minute for an emergency. Reasonable price. Would definitely recommend and use again in the future." },
};

export type Faq = { q: string; a: string };

export const faqGroups: { group: string; items: Faq[] }[] = [
  {
    group: "General",
    items: [
      { q: "What can a mobile mechanic fix at my home or work?", a: "Most common repairs can be done right where your vehicle is parked — diagnostics, brakes and suspension, electrical work, tune-ups, oil changes, heating and AC, and more. Larger jobs like transmission service and engine replacement can often be handled on site too. Call or text with your vehicle and the problem, and Josh will tell you straight what's possible." },
      { q: "Are you open on weekends?", a: "Yes — 406 Mobile Mechanic is open 7 days a week, Monday through Sunday, 8 AM to 9 PM. Saturdays and Sundays are regular working days." },
      { q: "Do you offer emergency and roadside help?", a: "Yes. If you've broken down at home, at work, or on the side of the road, call or text and we'll get to you as quickly as possible during business hours. When a vehicle can't be repaired on the spot, towing and transportation are available." },
    ],
  },
  {
    group: "Services",
    items: [
      { q: "Can you help when my car won't start in the cold?", a: "Absolutely. Cold-weather no-starts are one of the most common calls in Montana. Josh tests the battery, starter, charging system and fuel delivery on site to find the actual cause instead of guessing." },
      { q: "Can you diagnose electrical problems?", a: "Yes. Josh does methodical on-site electrical diagnostics, even on complex or modified vehicles with multiple battery systems or hard-to-reach wiring. He traces the problem step by step and shows you what he found." },
      { q: "Should I get a pre-purchase inspection before buying a used vehicle?", a: "It's one of the smartest things you can do. A pre-purchase inspection can uncover hidden problems before money changes hands, so you can negotiate or walk away. We can meet you where the vehicle is located." },
      { q: "Do you offer fleet service for businesses?", a: "Yes. We provide fleet vehicle maintenance for businesses and organizations across Montana, done at your location so your vehicles spend less time off the road. Call or text to talk about your fleet's needs." },
    ],
  },
  {
    group: "Pricing & Discounts",
    items: [
      { q: "Do you offer a military discount?", a: "Yes. We offer a military discount for service members and their families, including those at Malmstrom AFB. Just mention it when you reach out." },
      { q: "How do I get a quote?", a: "Call or text 406-799-0095 with your vehicle and what's going on. Josh will source parts and send a labor quote right away, so you know what to expect before any work begins." },
    ],
  },
];

export const allFaqs = faqGroups.flatMap((g) => g.items);

/** The four questions shown on the homepage. */
export const homeFaqs = [allFaqs[0]!, allFaqs[1]!, allFaqs[2]!, allFaqs[allFaqs.length - 1]!];

export type PhotoName = "hero" | "truck" | "lettering" | "towing" | "ac";

export const PHOTOS: Record<PhotoName, { widths: readonly number[]; width: number; height: number; alt: string }> = {
  hero: { widths: [640, 960, 1280, 1440], width: 1440, height: 1080, alt: "Customer's red sports car with the hood up getting repaired in falling snow, 406 Mobile Mechanic work truck parked behind" },
  truck: { widths: [480, 640, 960], width: 1200, height: 900, alt: "406 Mobile Mechanic work truck parked on a Great Falls street, ready for the next service call" },
  lettering: { widths: [480, 800, 1080], width: 1080, height: 1440, alt: "406 Mobile Mechanic vinyl lettering with 799-0095 on the side of Josh's black car" },
  towing: { widths: [480, 800], width: 1400, height: 1867, alt: "Vehicle loaded and strapped on the 406 Mobile Mechanic trailer behind the work truck" },
  ac: { widths: [480, 800, 1200], width: 1200, height: 1600, alt: "Infrared thermometer showing a 20.5°F vent temperature after an AC repair" },
};
